import React from "react";
import { Check } from "lucide-react";

/**
 * McqQuestion — single MCQ block (Heetch Pro dark theme).
 */
export function McqQuestion({
  title,
  subtitle,
  mode = "multi",
  options,
  value,
  onChange,
  maxSelect,
  testidPrefix,
  columns = 2,
}) {
  const normalised = options.map((o) =>
    typeof o === "string" ? { v: o, label: o } : { v: o.v, label: o.label || o.v, icon: o.icon }
  );

  const isActive = (v) => (mode === "single" ? value === v : Array.isArray(value) && value.includes(v));

  const toggle = (v) => {
    if (mode === "single") {
      onChange(v);
      return;
    }
    const current = Array.isArray(value) ? value : [];
    if (current.includes(v)) {
      onChange(current.filter((x) => x !== v));
    } else {
      if (maxSelect && current.length >= maxSelect) return;
      onChange([...current, v]);
    }
  };

  const selectedCount = mode === "multi" && Array.isArray(value) ? value.length : 0;

  return (
    <div className="hp-card p-5">
      <div className="mb-4">
        <h3 className="font-semibold text-[var(--text)] text-base leading-tight">{title}</h3>
        {subtitle && <p className="text-sm text-zinc-500 mt-1 leading-relaxed">{subtitle}</p>}
        {mode === "multi" && maxSelect && (
          <p className="text-xs text-emerald-400 font-semibold mt-2 tabular-nums">
            {selectedCount}/{maxSelect} sélectionné{selectedCount > 1 ? "s" : ""} · max {maxSelect}
          </p>
        )}
      </div>

      <div
        className={`grid gap-3 ${
          columns === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
        }`}
      >
        {normalised.map((opt) => {
          const active = isActive(opt.v);
          const disabled =
            mode === "multi" && maxSelect && !active && selectedCount >= maxSelect;
          return (
            <button
              key={opt.v}
              type="button"
              disabled={disabled}
              onClick={() => toggle(opt.v)}
              data-testid={testidPrefix ? `${testidPrefix}-${opt.v}` : undefined}
              className={`relative rounded-lg border px-5 py-4 text-left transition-colors active:scale-[0.98] min-h-[56px] flex items-center gap-3 ${
                active
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : disabled
                    ? "bg-[var(--surface-alt)]/40 text-zinc-600 border-[var(--border-soft)] cursor-not-allowed"
                    : "bg-[var(--surface)] text-zinc-200 border-[var(--border)] hover:border-zinc-700 hover:bg-[var(--surface-alt)]"
              }`}
            >
              {opt.icon && <span className="text-lg shrink-0">{opt.icon}</span>}
              <span className="text-sm font-medium leading-tight flex-1">{opt.label}</span>
              {mode === "multi" && active && (
                <Check className="w-4 h-4 shrink-0" strokeWidth={3} />
              )}
              {mode === "single" && active && (
                <div className="w-5 h-5 rounded-full bg-white grid place-items-center shrink-0">
                  <Check className="w-3 h-3 text-emerald-500" strokeWidth={4} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
